import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { apiGet } from "@/lib/api";

/**
 * Phase 11 — "Resources" preview on the home page.
 * Pulls 3 featured articles from the existing Learn endpoint.
 * Falls back to curated placeholders if the endpoint isn't available.
 */

const FALLBACK = [
  {
    slug: "term-vs-whole-life",
    title: "Term vs Whole Life: Which Is Right for Your Family?",
    category: "Getting Started",
    read_time: 6,
  },
  {
    slug: "annuity-basics",
    title: "Annuity Basics: Turning Savings into Lifetime Income",
    category: "Growing Wealth",
    read_time: 8,
  },
  {
    slug: "ltc-planning",
    title: "Planning for Long-Term Care Without Draining Assets",
    category: "Protecting Family",
    read_time: 7,
  },
];

export const EducationHubPreview = () => {
  const [articles, setArticles] = useState(FALLBACK);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet("/articles");
        if (!cancelled && Array.isArray(data) && data.length) {
          setArticles(data.slice(0, 3));
        }
      } catch {
        // keep fallback
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      id="resources-preview"
      data-testid="education-hub-preview"
      className="py-20 sm:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="lw-eyebrow">Education First</span>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]">
              Read before you <span className="italic">commit.</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-lw-muted">
              Plain-English explanations of the products, strategies, and
              trade-offs most relevant to your chapter of life — reviewed
              for compliance and updated regularly.
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/learn"
              data-testid="resources-preview-view-all"
              className="btn-ghost-navy text-sm"
            >
              View all resources <ArrowRight size={14} />
            </Link>
            <Link
              to="/glossary"
              data-testid="resources-preview-glossary"
              className="btn-ghost-navy text-sm"
            >
              Glossary
            </Link>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={`/learn/${a.slug}`}
              data-testid={`resource-card-${a.slug}`}
              className="lw-card group p-6 flex flex-col h-full"
            >
              <div className="w-10 h-10 rounded-lg bg-lw-gold/15 text-[#7f6218] flex items-center justify-center">
                <BookOpen size={18} />
              </div>
              <div className="mt-5 font-sans-ui text-[0.7rem] tracking-[0.2em] uppercase text-lw-gold">
                {a.category || "Learn"} · {a.read_time || 6} min read
              </div>
              <h3 className="mt-2 font-serif-display text-lg text-lw-navy leading-snug flex-1">
                {a.title}
              </h3>
              <span className="mt-6 inline-flex items-center gap-1.5 font-sans-ui text-sm font-semibold text-lw-navy group-hover:text-[#7f6218] transition-colors">
                Read article <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EducationHubPreview;
